import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import {
  TREQUEST_ATTACHMENT_DELETE,
  TREQUEST_ATTACHMENT_LIST,
  TREQUEST_ATTACHMENT_PRINT_QR,
  TREQUEST_ATTACHMENT_UPDATE,
  TREQUEST_ATTACHMENT_APPROVAL_LIST,
  TREQUEST_ATTACHMENT_ACTION,
  TREQUEST_ATTACHMENT_APPROVAL_ACTION,
  TAttachmentSerialNumber,
} from '@monorepo/types';
import { TUserPaginationOption } from '@monorepo/ui';
import { select, Store } from '@ngrx/store';
import { catchError, finalize, lastValueFrom, map, of } from 'rxjs';
import { TMachineMaintenanceState } from '../reducers';
import * as AttachmentActions from './attachment.actions';
import { ATTACHMENT_DEFAULT_PAGINATION_OPTION } from './attachment.model';
import * as AttachmentSelector from './attachment.selectors';

@Injectable({
  providedIn: 'root',
})
export class AttachmentFacade {
  constructor(
    private store: Store<TMachineMaintenanceState>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private mmDataService: MachineMaintenanceDataService,
  ) {}

  fetchAttachmentListing(payload: TREQUEST_ATTACHMENT_LIST) {
    this.store.dispatch(new AttachmentActions.FetchAttachmentList(payload));
  }

  resetAttachmentListing() {
    this.setAttachmentListingPaginationOption(ATTACHMENT_DEFAULT_PAGINATION_OPTION);
    this.fetchAttachmentListing({
      itemsPerPage: ATTACHMENT_DEFAULT_PAGINATION_OPTION.pageSize,
      page: ATTACHMENT_DEFAULT_PAGINATION_OPTION.pageNo,
      selectedFilters: [],
    });
  }

  getAttachmentListing() {
    return this.store.pipe(select(AttachmentSelector.getAttachmentList));
  }

  getAttachmentListingPaginationOption() {
    return this.store.pipe(select(AttachmentSelector.getAttachmentListingPaginationOption));
  }

  setAttachmentListingPaginationOption(payload: TUserPaginationOption) {
    return this.store.dispatch(new AttachmentActions.SetAttachmentListingPaginationOption(payload));
  }

  getAttachmentListingSearchStr() {
    return this.store.pipe(select(AttachmentSelector.getAttachmentListingSearchStr));
  }

  setAttachmentListingSearchStr(payload: string) {
    return this.store.dispatch(new AttachmentActions.SetAttachmentListingSearchStr(payload));
  }

  async deleteAttachments(payload: TREQUEST_ATTACHMENT_DELETE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.deleteAttachments(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async printAttachmentsQrCodes(payload: TREQUEST_ATTACHMENT_PRINT_QR) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.printAttachmentsQrCodes(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async getAttachmentUpdateInfo(factoryId: string) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.getMachineListFilterDropDownValues(factoryId).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  async updateAttachment(payload: TREQUEST_ATTACHMENT_UPDATE) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.updateAttachment(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  fetchAttachmentApprovalListing(payload: TREQUEST_ATTACHMENT_APPROVAL_LIST) {
    this.store.dispatch(new AttachmentActions.FetchAttachmentApprovalList(payload));
  }

  getAttachmentApprovalListing() {
    return this.store.pipe(select(AttachmentSelector.getAttachmentApprovalList));
  }

  async updateAttachmentByAction(payload: TREQUEST_ATTACHMENT_ACTION) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.updateAttachmentByAction(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async updateAttachmentApprovalByAction(payload: TREQUEST_ATTACHMENT_APPROVAL_ACTION) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.updateAttachmentApprovalByAction(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async getAttachmentDetailsBySerialNum(serialNum: TAttachmentSerialNumber) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.getAttachmentDetailsBySerialNum(serialNum).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }
}
